import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AnalysisResult, PdfRequest, PdfResult, PdfOptions } from '../types';
import { formatArea, formatDistance, formatDate, formatCoordinate } from '../utils/formatters';

// Default PDF options
const defaultOptions: PdfOptions = {
    includeMap: true,
    includeDetails: true,
    includeInterpretation: true,
    paperSize: 'A4',
    orientation: 'portrait',
    language: 'id',
};

/**
 * PDF Service - generates PDF reports
 */
export const pdfService = {
    /**
     * Generate PDF report from analysis result
     */
    async generatePdf(
        result: AnalysisResult,
        request?: Partial<PdfRequest>
    ): Promise<PdfResult> {
        const options = { ...defaultOptions, ...request?.options };
        const doc = new jsPDF({
            orientation: options.orientation,
            unit: 'mm',
            format: options.paperSize,
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 20;
        let yPosition = margin;

        // ===== COVER PAGE =====
        // Header gradient simulation
        doc.setFillColor(25, 118, 210);
        doc.rect(0, 0, pageWidth, 60, 'F');

        // Title
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.text('Laporan Analisis GIS', pageWidth / 2, 30, { align: 'center' });

        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text('GIS Spatial Analysis Results', pageWidth / 2, 42, { align: 'center' });

        // Reset text color
        doc.setTextColor(33, 33, 33);

        // Document info
        yPosition = 80;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Informasi Dokumen', margin, yPosition);
        yPosition += 10;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        const docInfo = [
            ['ID Analisis', result.id],
            ['Tanggal Dibuat', formatDate(result.createdAt)],
            ['Status', result.status === 'complete' ? 'Selesai' : result.status],
        ];

        docInfo.forEach(([label, value]) => {
            doc.text(`${label}: ${value}`, margin, yPosition);
            yPosition += 7;
        });

        if (result.data) {
            // Summary section
            yPosition += 10;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.text('Ringkasan Analisis', margin, yPosition);
            yPosition += 10;

            const summaryData = [
                ['Total Luas', formatArea(result.data.summary.totalArea)],
                ['Keliling', formatDistance(result.data.summary.perimeter)],
                ['Jumlah Bidang', `${result.data.summary.parcelCount} bidang`],
                ['Sistem Koordinat', result.data.summary.coordinateSystem],
                [
                    'Titik Pusat',
                    formatCoordinate(
                        result.data.summary.centerPoint.lat,
                        result.data.summary.centerPoint.lng
                    ),
                ],
            ];

            autoTable(doc, {
                startY: yPosition,
                head: [['Parameter', 'Nilai']],
                body: summaryData,
                margin: { left: margin, right: margin },
                headStyles: {
                    fillColor: [25, 118, 210],
                    textColor: 255,
                    fontStyle: 'bold',
                },
                styles: {
                    fontSize: 10,
                    cellPadding: 5,
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245],
                },
            });

            // ===== MAP PAGE =====
            if (options.includeMap && result.data.mapImage) {
                doc.addPage();
                yPosition = margin;

                doc.setFontSize(14);
                doc.setFont('helvetica', 'bold');
                doc.text('Map Preview', margin, yPosition);
                yPosition += 15;

                // Add map image
                try {
                    const imgWidth = pageWidth - margin * 2;
                    const imgHeight = (imgWidth * 3) / 4; // 4:3 aspect ratio
                    doc.addImage(
                        result.data.mapImage,
                        'PNG',
                        margin,
                        yPosition,
                        imgWidth,
                        imgHeight
                    );
                    yPosition += imgHeight + 10;
                } catch (error) {
                    doc.setFontSize(10);
                    doc.setTextColor(150, 150, 150);
                    doc.text('Peta tidak tersedia', margin, yPosition);
                    yPosition += 20;
                }

                // Bounding box info
                doc.setTextColor(33, 33, 33);
                doc.setFontSize(10);
                doc.setFont('helvetica', 'normal');
                const { north, south, east, west } = result.data.summary.boundingBox;
                doc.text(
                    `Batas Wilayah: N ${north.toFixed(4)}°, S ${south.toFixed(4)}°, E ${east.toFixed(4)}°, W ${west.toFixed(4)}°`,
                    margin,
                    yPosition
                );
            }

            // ===== DETAILS PAGE =====
            if (options.includeDetails && result.data.parcels.length > 0) {
                doc.addPage();
                yPosition = margin;

                doc.setFontSize(14);
                doc.setFont('helvetica', 'bold');
                doc.text('Feature Details', margin, yPosition);
                yPosition += 15;

                const parcelData = result.data.parcels.map((parcel, index) => [
                    `${index + 1}`,
                    parcel.name || `Bidang ${index + 1}`,
                    formatArea(parcel.area),
                    formatDistance(parcel.perimeter),
                    (parcel.properties.zoning as string) || '-',
                ]);

                autoTable(doc, {
                    startY: yPosition,
                    head: [['No', 'Nama', 'Luas', 'Keliling', 'Zonasi']],
                    body: parcelData,
                    margin: { left: margin, right: margin },
                    headStyles: {
                        fillColor: [25, 118, 210],
                        textColor: 255,
                        fontStyle: 'bold',
                    },
                    styles: {
                        fontSize: 9,
                        cellPadding: 4,
                    },
                    alternateRowStyles: {
                        fillColor: [245, 245, 245],
                    },
                });
            }

            // ===== INTERPRETATION PAGE =====
            if (options.includeInterpretation && result.data.interpretation) {
                doc.addPage();
                yPosition = margin;

                doc.setFontSize(14);
                doc.setFont('helvetica', 'bold');
                doc.text('Interpretasi Hasil Analisis', margin, yPosition);
                yPosition += 15;

                // Summary
                doc.setFontSize(10);
                doc.setFont('helvetica', 'normal');
                const summaryLines = doc.splitTextToSize(
                    result.data.interpretation.summary,
                    pageWidth - margin * 2
                );
                doc.text(summaryLines, margin, yPosition);
                yPosition += summaryLines.length * 5 + 15;

                // Details
                doc.setFont('helvetica', 'bold');
                doc.text('Detail Analisis:', margin, yPosition);
                yPosition += 8;

                doc.setFont('helvetica', 'normal');
                result.data.interpretation.details.forEach((detail) => {
                    if (yPosition > pageHeight - 40) {
                        doc.addPage();
                        yPosition = margin;
                    }

                    doc.setFont('helvetica', 'bold');
                    doc.text(`• ${detail.title}`, margin, yPosition);
                    yPosition += 6;

                    doc.setFont('helvetica', 'normal');
                    const detailLines = doc.splitTextToSize(
                        detail.description,
                        pageWidth - margin * 2 - 5
                    );
                    doc.text(detailLines, margin + 5, yPosition);
                    yPosition += detailLines.length * 5 + 5;
                });

                // Recommendations
                yPosition += 10;
                if (yPosition > pageHeight - 60) {
                    doc.addPage();
                    yPosition = margin;
                }

                doc.setFont('helvetica', 'bold');
                doc.text('Rekomendasi:', margin, yPosition);
                yPosition += 8;

                doc.setFont('helvetica', 'normal');
                result.data.interpretation.recommendations.forEach((rec, index) => {
                    if (yPosition > pageHeight - 20) {
                        doc.addPage();
                        yPosition = margin;
                    }
                    const recLines = doc.splitTextToSize(
                        `${index + 1}. ${rec}`,
                        pageWidth - margin * 2
                    );
                    doc.text(recLines, margin, yPosition);
                    yPosition += recLines.length * 5 + 3;
                });
            }
        }

        // ===== FOOTER ON ALL PAGES =====
        const totalPages = doc.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(150, 150, 150);
            doc.text(
                `Halaman ${i} dari ${totalPages}`,
                pageWidth / 2,
                pageHeight - 10,
                { align: 'center' }
            );
            doc.text(
                `Dibuat dengan GIS Analysis`,
                pageWidth - margin,
                pageHeight - 10,
                { align: 'right' }
            );
        }

        // Generate blob
        const blob = doc.output('blob');
        const filename = `analisis-gis-${result.id}.pdf`;

        return {
            blob,
            filename,
            size: blob.size,
            generatedAt: new Date(),
        };
    },

    /**
     * Preview PDF (returns data URL)
     */
    async previewPdf(result: AnalysisResult): Promise<string> {
        const pdfResult = await this.generatePdf(result);
        return URL.createObjectURL(pdfResult.blob);
    },

    /**
     * Download PDF
     */
    downloadPdf(result: PdfResult): void {
        const url = URL.createObjectURL(result.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    },
};

export default pdfService;
